import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Settings2, ToggleRight, Boxes, SlidersHorizontal, RefreshCw, GripVertical, Pencil, Eye, EyeOff } from "lucide-react";

const defaults = [
  ["pos.allow_negative_stock", false, "Izinkan stok negatif pada POS"],
  ["pos.require_shift", true, "Kasir wajib memiliki shift aktif"],
  ["inventory.enable_waste", true, "Aktifkan pencatatan waste"],
  ["inventory.enable_stock_opname", true, "Aktifkan stock opname"],
  ["purchasing.require_receiving", true, "Receiving wajib sebelum stok pembelian masuk"],
  ["accounting.auto_journal", true, "Posting jurnal otomatis"],
  ["hr.enable_payroll", true, "Aktifkan payroll"],
];


const SIDEBAR_MENU_ITEMS = [
  ["/command-center", "Command Center"], ["/dashboard", "Dashboard"],
  ["/pos", "POS Kasir"], ["/shifts", "Shift Kasir"],
  ["/transactions", "Transaksi"], ["/inventory", "Inventory"],
  ["/stock-in", "Stock In"], ["/stock-out", "Stock Out"],
  ["/stock-request", "Stock Request"], ["/transfers", "Stock Transfer"],
  ["/purchasing", "Purchasing"], ["/promos", "Promo & Diskon"],
  ["/hr", "HR Center"], ["/payroll", "Payroll & Kasbon"],
  ["/finance", "Finance"], ["/expenses", "Pengeluaran"],
  ["/reports", "Laporan"], ["/reconciliation", "Rekonsiliasi"],
  ["/sheets", "Google Sheets"], ["/bom-hpp", "Bahan & HPP"],
  ["/outlets", "Outlet"], ["/audit", "Audit Log"],
  ["/master-data", "Master Data"], ["/configuration", "Configuration"],
  ["/access-control", "Role & Hak Akses"],
].map(([to, label]) => ({ to, label }));

function SidebarSettingsCard({ settings, onSave }) {
  const [order, setOrder] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [labels, setLabels] = useState({});
  const [saving, setSaving] = useState(false);



  useEffect(() => {
    const validPaths = new Set(SIDEBAR_MENU_ITEMS.map(x => x.to));
    const savedOrder = Array.isArray(settings.order) ? settings.order : [];
    setOrder([...savedOrder.filter(x => validPaths.has(x)),
      ...SIDEBAR_MENU_ITEMS.map(x => x.to).filter(x => !savedOrder.includes(x))]);
    setHidden(Array.isArray(settings.hidden) ? settings.hidden.filter(x => validPaths.has(x)) : []);
    setLabels(settings.labels && typeof settings.labels === "object" ? settings.labels : {});
  }, [settings]);

  const [dragPath, setDragPath] = useState(null);
  const [overPath, setOverPath] = useState(null);

  function dropOn(path) {
    if (!dragPath || dragPath === path) {
      setDragPath(null);
      setOverPath(null);
      return;
    }
    const next = [...order];
    const from = next.indexOf(dragPath);
    const to = next.indexOf(path);
    if (from >= 0 && to >= 0) {
      next.splice(from, 1);
      next.splice(to, 0, dragPath);
      setOrder(next);
    }
    setDragPath(null);
    setOverPath(null);
  }

  function rename(path, original) {
    const current = labels[path] || original;
    const value = window.prompt("Nama menu baru:", current);
    if (value === null) return;
    const clean = value.trim();
    setLabels(prev => {
      const next = { ...prev };
      if (!clean || clean === original) delete next[path];
      else next[path] = clean;
      return next;
    });
  }

  async function save() {
    setSaving(true);
    try {
      // Struktur opsional untuk hierarki submenu di arsitektur mendatang.
      // UI saat ini tetap hanya mengatur menu utama.
      const hierarchy =
        settings.hierarchy && typeof settings.hierarchy === "object"
          ? settings.hierarchy
          : {
              version: 2,
              parentByPath: {},
              childOrderByParent: {},
            };

      await onSave({ order, hidden, labels, hierarchy });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Settings2 className="w-4 h-4"/>Pengaturan Sidebar
        </CardTitle>
        <p className="text-sm text-slate-500">
          Atur urutan, nama, dan visibilitas menu untuk seluruh tenant.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {order.map((path, index) => {
          const item = SIDEBAR_MENU_ITEMS.find(x => x.to === path);
          if (!item) return null;
          const isHidden = hidden.includes(path);
          return (
            <div key={path}
              onDragOver={e => { e.preventDefault(); setOverPath(path); }}
              onDrop={e => { e.preventDefault(); dropOn(path); }}
              className={`flex items-center gap-2 rounded-lg border p-2 mb-2 transition ${
                overPath === path && dragPath !== path ? "border-orange-500 bg-orange-50/50" : ""
              } ${dragPath === path ? "opacity-50" : ""}`}>
              <button type="button" draggable
                aria-label={`Geser menu ${labels[path] || item.label}`}
                title="Tekan dan geser untuk mengurutkan"
                onDragStart={e => {
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", path);
                  setDragPath(path);
                }}
                onDragEnd={() => { setDragPath(null); setOverPath(null); }}
                className="cursor-grab active:cursor-grabbing touch-none rounded p-1 text-slate-400 hover:text-orange-500"
              >
                <GripVertical className="w-5 h-5"/>
              </button>
              <div className="min-w-0 flex-1">
                <div className={`text-sm font-medium ${isHidden ? "text-slate-400 line-through" : ""}`}>
                  {labels[path] || item.label}
                </div>
                <div className="text-xs text-slate-400">{path}</div>
              </div>
              <Button type="button" size="icon" variant="outline" title="Ubah nama"
                onClick={() => rename(path, item.label)}>
                <Pencil className="w-4 h-4"/>
              </Button>
              <Button type="button" size="icon" variant="outline"
                title={isHidden ? "Tampilkan" : "Sembunyikan"}
                onClick={() => setHidden(prev => isHidden ? prev.filter(x => x !== path) : [...prev, path])}>
                {isHidden ? <Eye className="w-4 h-4"/> : <EyeOff className="w-4 h-4"/>}
              </Button>
            </div>
          );
        })}
        <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
          {saving ? "Menyimpan..." : "Simpan Pengaturan Sidebar"}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function ConfigurationPage() {
  const [cfg, setCfg] = useState({ settings: [], features: [], rules: [], templates: [] });
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState("tenant");
  const [template, setTemplate] = useState({ key: "fnb_default", name: "F&B Default", business_type: "fnb" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/configuration?scope_type=${scope}`);
      setCfg(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat konfigurasi"); }
    finally { setLoading(false); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const featureMap = Object.fromEntries((cfg.features || []).map(x => [x.key, x]));
  async function setFeature(key, value) {
    try {
      await api.put("/configuration/features", { key, value, scope_type: scope, active: true });
      await load();
      toast.success("Feature toggle disimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  }

  async function saveTemplate() {
    try {
      await api.put("/configuration/templates", { ...template, config: { business_type: template.business_type } });
      await load();
      toast.success("Business template disimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan template"); }
  }

  return (
    <div className="page-shell">
      <div data-workspace-redundant-header><div>
        <h1 className="text-2xl font-extrabold font-display">Platform Configuration</h1></div>
        <p className="text-slate-500 text-sm">Konfigurasi per tenant agar variasi bisnis tidak memerlukan perubahan source code.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><SlidersHorizontal className="w-4 h-4"/>Scope</CardTitle></CardHeader>
        <CardContent className="flex gap-3 items-center">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tenant">Tenant</SelectItem>
              <SelectItem value="business_unit">Business Unit</SelectItem>
              <SelectItem value="brand">Brand</SelectItem>
              <SelectItem value="outlet">Outlet</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className="w-4 h-4 mr-1"/>Refresh</Button>
        </CardContent>
      </Card>

      {scope === "tenant" && (
        <SidebarSettingsCard
          settings={(cfg.settings || []).find(x => x.key === "ui.sidebar")?.value || {}}
          onSave={async (value) => {
            try {
              await api.put("/configuration/settings", {
                key: "ui.sidebar",
                value,
                scope_type: "tenant",
                active: true,
                description: "Pengaturan tampilan sidebar Mazboy Hub"
              });
              await load();
              toast.success("Pengaturan sidebar berhasil disimpan");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Gagal menyimpan sidebar");
            }
          }}
        />
      )}

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ToggleRight className="w-4 h-4"/>Feature Toggle</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {defaults.map(([key, def, desc]) => (
            <div key={key} className="flex items-center justify-between rounded-lg border p-3">
              <div><div className="font-medium text-sm">{desc}</div><div className="text-xs text-slate-500">{key}</div></div>
              <Switch checked={featureMap[key]?.value ?? def} onCheckedChange={v => setFeature(key, v)} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Boxes className="w-4 h-4"/>Business Templates</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label>Key</Label><Input value={template.key} onChange={e=>setTemplate({...template,key:e.target.value})}/></div>
            <div><Label>Nama</Label><Input value={template.name} onChange={e=>setTemplate({...template,name:e.target.value})}/></div>
            <div><Label>Tipe bisnis</Label><Select value={template.business_type} onValueChange={v=>setTemplate({...template,business_type:v})}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{["fnb","retail","manufacturing","services","distribution","logistics","workforce","rental"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <Button onClick={saveTemplate} className="bg-orange-500 hover:bg-orange-600">Simpan Template</Button>
          <div className="flex flex-wrap gap-2">{(cfg.templates||[]).map(t=><Badge key={t.id || t.key} variant="outline">{t.name} · {t.business_type}</Badge>)}</div>
        </CardContent>
      </Card>
    </div>
  );
}
